/**
 * Stops one broken part of a page from blanking the whole screen. Shows what
 * went wrong (so it can be reported) and a way to carry on. Drafts are kept on
 * this computer and on the server as you type, so reloading loses nothing.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  /** Where this is, e.g. "the form" or "the preview". */
  what: string;
  children: ReactNode;
  /** Small boundary (e.g. the preview): a one-line message instead of a full box. */
  compact?: boolean;
}

interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept in the browser console for the report; never sent anywhere.
    console.error(`[${this.props.what}]`, error, info.componentStack);
  }

  private retry = () => this.setState({ error: null });

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const detail = `${error.name}: ${error.message}`;
    return (
      <div role="alert" className={`rounded border border-red-300 bg-red-50 text-sm text-red-950 ${this.props.compact ? 'm-2 p-2' : 'm-4 p-4'}`} data-testid="error-boundary">
        <p className="font-semibold">Something went wrong in {this.props.what}.</p>
        <p className="mt-1">Your typing is saved. Press <b>Try again</b>; if it comes back, press <b>Reload the page</b>. Please send this message to the developer:</p>
        <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-white p-2 font-mono text-xs">{detail}</pre>
        <div className="mt-2 flex gap-2">
          <button type="button" className="rounded border border-slate-400 bg-white px-3 py-1" onClick={this.retry}>Try again</button>
          <button type="button" className="rounded bg-slate-900 px-3 py-1 text-white" onClick={() => location.reload()}>Reload the page</button>
        </div>
      </div>
    );
  }
}
